import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import styled from 'styled-components';
import { Card, Button } from '../../PageStyles';
import { BADGE_TIER_CONFIG } from '../../../types/learningCenter';
import { FaMedal, FaCheckCircle, FaClock, FaLock, FaPlayCircle } from 'react-icons/fa';
// Styled Components
export const TutorialCard = styled(Card) `
  background: var(--bg-secondary);
  border: 1px solid ${props => props.$completed ? '#22c55e' : props.$locked ? 'var(--border-color)' : 'var(--border-color)'};
  padding: 24px;
  padding-right: 90px; /* Make room for badge preview in top-right */
  transition: all 0.2s;
  position: relative;
  opacity: ${props => props.$locked ? 0.6 : 1};
  cursor: ${props => props.$locked ? 'not-allowed' : 'default'};

  &:hover {
    border-color: ${props => props.$locked ? 'var(--border-color)' : 'var(--accent-color)'};
    transform: ${props => props.$locked ? 'none' : 'translateY(-2px)'};
    box-shadow: ${props => props.$locked ? 'none' : '0 8px 24px rgba(0, 0, 0, 0.3)'};
  }

  ${props => props.$completed && `
    border-color: #22c55e;
    border-width: 2px;
  `}

  .tutorial-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 16px;
    gap: 12px;
  }

  .tutorial-title {
    font-size: 18px;
    font-weight: 700;
    color: var(--text-primary);
    margin: 0 0 8px 0;
  }

  .tutorial-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 16px;
    font-size: 12px;
    color: var(--text-secondary);
  }

  .tutorial-description {
    color: var(--text-secondary);
    line-height: 1.6;
    margin-bottom: 20px;
    font-size: 14px;
  }
`;
export const TutorialsGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 24px;
`;
export const BadgePreview = styled.div `
  position: absolute;
  top: 16px;
  right: 16px;
  width: 60px;
  height: 60px;
  background: ${props => props.$color};
  border: 2px solid var(--border-color);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;

  svg {
    font-size: 28px;
    color: var(--bg-primary);
  }
`;
export const CompletedBadge = styled.div `
  position: absolute;
  top: -8px;
  right: -8px;
  width: 40px;
  height: 40px;
  background: #22c55e;
  border: 4px solid var(--bg-primary);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;

  svg {
    font-size: 16px;
    color: white;
  }
`;
export const ComingSoonBadge = styled(CompletedBadge) `
  background: #f59e0b;
`;
export const LockOverlay = styled.div `
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(2px);
  border-radius: inherit;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 20px;
  z-index: 3;

  svg {
    font-size: 48px;
    color: rgba(255, 255, 255, 0.7);
  }

  p {
    color: rgba(255, 255, 255, 0.9);
    font-size: 14px;
    font-weight: 600;
    text-align: center;
    margin: 0;
    line-height: 1.5;

    &:first-of-type {
      font-size: 16px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 1px;
    }
  }
`;
export const ComingSoonOverlay = styled(LockOverlay) `
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.92), rgba(251, 146, 60, 0.92));

  svg {
    color: white;
    font-size: 56px;
  }

  p {
    color: white;

    &:first-of-type {
      font-size: 18px;
    }

    &:last-of-type {
      font-size: 13px;
      opacity: 0.95;
      margin-top: 4px;
    }
  }
`;
export const MetaBadge = styled.span `
  padding: 4px 8px;
  background: ${props => props.$color ? `${props.$color}20` : 'var(--bg-tertiary)'};
  border: 1px solid ${props => props.$color || 'var(--border-color)'};
  border-radius: 4px;
  color: ${props => props.$color || 'var(--text-secondary)'};
  font-weight: 600;
  text-transform: uppercase;
  font-size: 11px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;
const StartButton = styled(Button) `
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 600;
`;
export const TutorialCardDisplay = ({ tutorial, completed, locked, comingSoon, userRoleLevel, onStart, cardId }) => {
    return (_jsxs(TutorialCard, { id: cardId, "$completed": completed, "$locked": locked || comingSoon, children: [_jsx(BadgePreview, { "$color": BADGE_TIER_CONFIG[tutorial.badge.tier].color, children: _jsx(FaMedal, {}) }), completed && !comingSoon && (_jsx(CompletedBadge, { children: _jsx(FaCheckCircle, {}) })), comingSoon && (_jsx(ComingSoonBadge, { children: _jsx(FaClock, {}) })), locked && !comingSoon && (_jsxs(LockOverlay, { children: [_jsx(FaLock, {}), _jsx("p", { children: userRoleLevel < tutorial.minRoleLevel
                            ? 'Requires Higher Access Level'
                            : 'Complete Required Tutorials First' })] })), comingSoon && (_jsxs(ComingSoonOverlay, { children: [_jsx(FaClock, {}), _jsx("p", { children: "Coming Soon" }), _jsx("p", { children: "This feature is currently in development" })] })), _jsx("div", { className: "tutorial-header", children: _jsxs("div", { children: [_jsx("h4", { className: "tutorial-title", children: tutorial.title }), _jsxs("div", { className: "tutorial-meta", children: [_jsx(MetaBadge, { "$color": tutorial.difficulty === 'beginner' ? '#22c55e' :
                                        tutorial.difficulty === 'intermediate' ? '#f59e0b' : '#ef4444', children: tutorial.difficulty }), !comingSoon && (_jsxs(_Fragment, { children: [_jsxs(MetaBadge, { children: [_jsx(FaClock, {}), " ", tutorial.estimatedMinutes, " min"] }), _jsxs(MetaBadge, { children: [tutorial.totalChapters, " ", tutorial.totalChapters === 1 ? 'chapter' : 'chapters'] })] })), _jsxs(MetaBadge, { "$color": BADGE_TIER_CONFIG[tutorial.badge.tier].color, children: [_jsx(FaMedal, {}), " ", tutorial.badge.tier] })] })] }) }), _jsx("p", { className: "tutorial-description", children: tutorial.description }), _jsx(StartButton, { onClick: () => onStart(tutorial), disabled: locked || comingSoon, variant: completed ? 'secondary' : 'primary', children: comingSoon ? (_jsxs(_Fragment, { children: [_jsx(FaClock, {}), " Coming Soon"] })) : completed ? (_jsxs(_Fragment, { children: [_jsx(FaCheckCircle, {}), " Completed - Replay"] })) : (_jsxs(_Fragment, { children: [_jsx(FaPlayCircle, {}), " ", locked ? 'Locked' : 'Start Tutorial'] })) })] }));
};
